import { Icone } from '../components/Icone';
import { Chargement, EcranEnErreur } from '../components/ui/Primitives';
import { LogoMarque } from '../marque/LogoMarque';
import {
  urlDocumentApprenant,
  useMesCertificatsApprenant,
  type CertificatApprenant,
  type MesCertificatsApprenant,
} from '../lib/useEspaceApprenant';
import { ErreurApi } from '../lib/api';

/**
 * « Certificats & attestations » (écran `certificates`, TF-455).
 *
 * Seuls les documents réellement délivrés à l'apprenant apparaissent ici :
 * certificat de réalisation et attestation d'entrée en formation, une fois
 * validés par l'organisme. Le certificat le plus récent est mis en avant, à
 * télécharger en PDF ou à ajouter au profil LinkedIn.
 *
 * Règles tenues sans exception :
 * — le certificat de réalisation n'atteste que les heures **émargées**, jamais
 *   le volume prévu au programme ;
 * — il n'existe qu'en mode OF. En CFA, c'est le CERFA et le diplôme qui font
 *   foi, et la page le dit plutôt que d'afficher une case vide ;
 * — le lien LinkedIn vient de l'API : rien ne part sans que l'apprenant le
 *   valide sur LinkedIn.
 *
 * La maquette montrait des notes, des compétences, un QR code et une
 * vérification « blockchain » : la base n'en porte aucune, ces blocs sont donc
 * absents plutôt qu'inventés.
 */

function dateLongue(iso: string): string {
  const [annee, mois, jour] = iso.split('-').map(Number);
  if (!annee || !mois || !jour) return iso;
  return new Date(Date.UTC(annee, mois - 1, jour)).toLocaleDateString('fr-FR', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Le logo LinkedIn de la maquette, plein. */
function LogoLinkedin({ className }: { className: string }): JSX.Element {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.761 0 5-2.239 5-5v-14c0-2.761-2.239-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
    </svg>
  );
}

const CLASSE_LINKEDIN = 'bg-[#0A66C2] text-white transition hover:bg-[#084c95]';

function Tuile({
  titre,
  valeur,
  suffixe,
  legende,
  className = '',
  taille = 'text-2xl sm:text-3xl',
}: {
  titre: string;
  valeur: string;
  suffixe?: string;
  legende: string;
  className?: string;
  /** Un mot long (« Apprentissage ») tient dans une demi-largeur de téléphone. */
  taille?: string;
}): JSX.Element {
  return (
    <div className={`min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-5 ${className}`}>
      <div className="text-xs uppercase tracking-wide text-codialis-gray">{titre}</div>
      <div className={`font-display mt-1 break-words font-extrabold text-codialis-navy ${taille}`}>
        {valeur}
        {suffixe && <span className="text-base text-codialis-gray">{suffixe}</span>}
      </div>
      <div className="mt-2 text-xs text-codialis-gray">{legende}</div>
    </div>
  );
}

function CertificatEnAvant({
  certificat,
  donnees,
}: {
  certificat: CertificatApprenant;
  donnees: MesCertificatsApprenant;
}): JSX.Element {
  const delivreLe = certificat.recuLe ? dateLongue(certificat.recuLe) : null;
  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-card" aria-label="Dernier certificat délivré">
      <div className="grid grid-cols-1 lg:grid-cols-5">
        <div className="min-w-0 bg-gradient-to-br from-codialis-sand to-white p-4 sm:p-8 lg:col-span-3 lg:p-12">
          <div className="relative overflow-hidden rounded-lg border-8 border-double border-codialis-navy/15 bg-white px-4 py-6 shadow-xl sm:p-8 lg:p-10">
            <div className="gradient-mint absolute left-0 right-0 top-0 h-2" />
            <div className="text-center">
              <LogoMarque className="mx-auto mb-2 h-8 w-auto" />
              <div className="mb-6 text-[10px] tracking-[0.3em] text-codialis-gray">CERTIFICAT DE RÉALISATION</div>
              <p className="mb-3 text-xs text-codialis-gray">Le présent certificat atteste que</p>
              <h2 className="font-display mb-1 break-words text-2xl font-extrabold text-codialis-navy sm:text-3xl">
                {donnees.beneficiaire}
              </h2>
              <p className="mb-6 text-xs text-codialis-gray">a suivi l’action de formation</p>
              {donnees.formation && (
                <h3 className="font-display mb-4 break-words text-lg font-bold text-codialis-blue sm:text-xl">
                  {donnees.formation.titre}
                </h3>
              )}
              <div className="my-6 grid grid-cols-1 gap-3 text-xs sm:grid-cols-3 sm:gap-4">
                <div className="min-w-0">
                  <div className="uppercase tracking-wider text-codialis-gray">Délivré le</div>
                  <div className="mt-0.5 font-bold text-codialis-navy">{delivreLe ?? '—'}</div>
                </div>
                <div className="min-w-0">
                  <div className="uppercase tracking-wider text-codialis-gray">Certification visée</div>
                  <div className="mt-0.5 font-bold text-codialis-navy">
                    {donnees.formation ? `RNCP ${donnees.formation.rncp}` : '—'}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="uppercase tracking-wider text-codialis-gray">Référence</div>
                  <div className="mt-0.5 font-mono font-bold text-codialis-navy">{donnees.reference}</div>
                </div>
              </div>
              <p className="border-t border-codialis-navy/10 pt-4 text-[10px] text-codialis-gray">
                Aperçu · le PDF établi par {donnees.organisme} fait foi
              </p>
            </div>
          </div>
        </div>

        <div className="min-w-0 space-y-5 p-4 sm:p-6 lg:col-span-2 lg:p-8">
          <div>
            <span className="badge-success rounded px-2 py-0.5 text-[10px] font-bold uppercase">Délivré</span>
            <h3 className="font-display mt-2 text-2xl font-bold text-codialis-navy">{certificat.intitule}</h3>
            <p className="mt-1 text-sm text-codialis-gray">
              {delivreLe ? `Délivré le ${delivreLe}` : 'Délivré'} par {donnees.organisme}
            </p>
          </div>

          {certificat.linkedin && (
            <div className="rounded-xl bg-gray-50 p-4 text-xs text-codialis-gray">
              <div className="mb-1 font-bold uppercase tracking-wider text-codialis-gray">Sur LinkedIn</div>
              <p>
                « Ajouter à LinkedIn » ouvre votre profil, rubrique « Licences et certifications », avec ce certificat
                prérempli : intitulé, organisme, mois de délivrance et référence. Rien n’est publié tant que vous ne
                l’enregistrez pas sur LinkedIn.
              </p>
            </div>
          )}

          <div className={`grid gap-2 ${certificat.linkedin ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {certificat.telechargeable && (
              <a
                href={urlDocumentApprenant(certificat.id)}
                className="flex min-w-0 flex-col items-center gap-1 rounded-xl bg-codialis-navy py-3 text-xs font-semibold text-white transition hover:bg-codialis-deep"
              >
                <Icone nom="telecharger" className="h-5 w-5" />
                PDF
              </a>
            )}
            {certificat.linkedin && (
              <a
                href={certificat.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ajouter à LinkedIn (nouvel onglet)"
                className={`flex min-w-0 flex-col items-center gap-1 rounded-xl py-3 text-xs font-semibold ${CLASSE_LINKEDIN}`}
              >
                <LogoLinkedin className="h-5 w-5" />
                LinkedIn
              </a>
            )}
            {certificat.telechargeable && (
              <a
                href={urlDocumentApprenant(certificat.id, true)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-w-0 flex-col items-center gap-1 rounded-xl border border-gray-200 py-3 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
              >
                <Icone nom="oeil" className="h-5 w-5" />
                Consulter
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function CarteDocument({ document }: { document: CertificatApprenant }): JSX.Element {
  const certificat = document.type === 'CERTIFICAT';
  return (
    <div className="min-w-0 overflow-hidden rounded-2xl bg-white shadow-card transition hover:-translate-y-1">
      <div className="relative flex h-24 items-center justify-center bg-gradient-to-br from-codialis-sky to-codialis-blue">
        <Icone nom={certificat ? 'medaille' : 'chapeauDiplome'} className="h-10 w-10 text-white" />
        <span className="badge-success absolute right-2 top-2 rounded px-2 py-0.5 text-[10px] font-bold uppercase">
          Délivré
        </span>
      </div>
      <div className="p-4">
        <h4 className="font-display mb-1 text-sm font-bold text-codialis-navy">{document.intitule}</h4>
        <div className="text-xs text-codialis-gray">{certificat ? 'Certificat · fin de parcours' : 'Attestation'}</div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs">
          <span className="text-codialis-gray">{document.recuLe ? dateLongue(document.recuLe) : ''}</span>
          <div className="flex flex-wrap gap-2">
            {document.linkedin && (
              <a
                href={document.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Ajouter « ${document.intitule} » à LinkedIn (nouvel onglet)`}
                className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 font-semibold ${CLASSE_LINKEDIN}`}
              >
                <LogoLinkedin className="h-3.5 w-3.5" />
                LinkedIn
              </a>
            )}
            {document.telechargeable ? (
              <a
                href={urlDocumentApprenant(document.id)}
                className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg bg-codialis-navy px-3 font-semibold text-white transition hover:bg-codialis-deep"
              >
                <Icone nom="telecharger" className="h-3.5 w-3.5" />
                PDF
              </a>
            ) : (
              <span className="text-codialis-gray">Fichier pas encore disponible</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Information({ icone, texte }: { icone: 'ampoule' | 'sablier'; texte: string }): JSX.Element {
  return (
    <div className="flex items-start gap-2 rounded-2xl bg-white p-4 text-sm text-codialis-gray shadow-card sm:p-5">
      <Icone nom={icone} className="mt-0.5 h-4 w-4 shrink-0 text-codialis-sky" />
      <p className="min-w-0">{texte}</p>
    </div>
  );
}

export function CertificatsPage(): JSX.Element {
  const { data, isLoading, error } = useMesCertificatsApprenant();

  if (isLoading) return <Chargement libelle="Chargement de vos certificats…" />;
  if (error) {
    return (
      <EcranEnErreur
        titre="Certificats & attestations"
        message={error instanceof ErreurApi ? error.message : 'Vos certificats n’ont pas pu être chargés.'}
      />
    );
  }
  if (!data) return <EcranEnErreur titre="Certificats & attestations" message="Vos certificats n’ont pas pu être chargés." />;

  const { emargement, ecf } = data;
  const enAvant = data.certificats.find((document) => document.type === 'CERTIFICAT') ?? null;
  const tuiles = ecf ? 4 : 3;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <div className="min-w-0">
        <h1 className="font-display text-3xl font-extrabold text-codialis-navy">Certificats &amp; attestations</h1>
        <p className="mt-1 text-codialis-gray">
          Documents délivrés par {data.organisme} au titre de votre parcours
          {data.formation ? ` · ${data.formation.titre}` : ''}
        </p>
      </div>

      <div className={`grid grid-cols-2 gap-4 ${tuiles === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
        <Tuile
          titre="Documents délivrés"
          valeur={String(data.certificats.length)}
          legende="mis à votre disposition par l’organisme"
        />
        <Tuile
          titre="Heures émargées"
          valeur={String(emargement.heuresSuivies).replace('.', ',')}
          suffixe="h"
          legende="ce que le certificat de réalisation atteste"
        />
        {ecf && (
          <Tuile
            titre="ECF acquises"
            valeur={String(ecf.acquises)}
            suffixe={`/${ecf.attendues}`}
            legende="évaluations en cours de formation"
          />
        )}
        <Tuile
          titre="Parcours"
          valeur={data.mode === 'CFA' ? 'Apprentissage' : 'Formation continue'}
          legende={`${data.dateEntree ? `Entrée le ${dateLongue(data.dateEntree)}` : 'Entrée non renseignée'}${
            data.dateFinPrevue ? ` · fin prévue le ${dateLongue(data.dateFinPrevue)}` : ''
          }`}
          className={tuiles === 3 ? 'col-span-2 lg:col-span-1' : ''}
          taille={tuiles === 3 ? 'text-2xl' : 'text-base sm:text-2xl'}
        />
      </div>

      {data.mode === 'CFA' ? (
        <Information
          icone="ampoule"
          texte="En apprentissage, l’organisme ne délivre pas de certificat de réalisation : le contrat CERFA et le diplôme préparé font foi. Les attestations ci-dessous sont celles émises pour votre dossier."
        />
      ) : data.enAttente ? (
        <Information icone="sablier" texte={data.enAttente} />
      ) : !enAvant && emargement.heuresSuivies === 0 ? (
        <Information
          icone="ampoule"
          texte="Aucune heure n’est encore constatée par l’émargement : un certificat de réalisation établi aujourd’hui n’attesterait aucune heure de formation."
        />
      ) : null}

      {enAvant && <CertificatEnAvant certificat={enAvant} donnees={data} />}

      {data.certificats.length === 0 ? (
        <div className="rounded-2xl bg-white p-4 text-center text-sm text-codialis-gray shadow-card sm:p-8">
          Aucune attestation n’est encore mise à votre disposition. Elle est délivrée par l’organisme à la fin de votre
          parcours.
        </div>
      ) : (
        <div>
          <h3 className="font-display mb-4 text-xl font-bold text-codialis-navy">Tous mes documents</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.certificats.map((document) => (
              <CarteDocument key={document.id} document={document} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
